import { CurrencyPipe, DatePipe, formatCurrency } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, inject, Input, LOCALE_ID, OnDestroy, OnInit, signal, ViewChild } from "@angular/core";
import { ShowHideDirective } from "@ngbracket/ngx-layout/extended";
import { LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { BusyIndicatorComponent, formatAbbreviatedNumber } from "@tylertech/common-components-module";
import { LoggingService, UrlService } from "@tylertech/core-module";
import { ForgeCardModule, ForgeIconButtonModule, ForgeToolbarModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { IntentService } from "@tylertech/intents-module";
import { Utilities } from "@tylertech/shared-module";
import { ExecutionResult, IChartCriteria } from "@tylertech/tyler-api-types";
import { ActiveElement, Chart, ChartConfiguration, ChartEvent, ChartType, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "accounts-payable-outstanding-invoices-chart",
    templateUrl: "./outstandingInvoicesChart.tmpl.html",
    imports: [
        BusyIndicatorComponent,
        ChartJsContainerComponent,
        CurrencyPipe,
        DatePipe,
        LayoutAlignDirective,
        LayoutDirective,
        ShowHideDirective,
        ForgeCardModule,
        ForgeToolbarModule,
        ForgeIconButtonModule,
        ForgeTooltipModule,
    ]
})
export class OutstandingInvoicesChartComponent implements OnDestroy, OnInit
{
    private readonly locale = inject(LOCALE_ID);
    private readonly $chartService = inject(ChartService);
    private readonly $chartDialogService = inject(ChartDialogService);
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $loggingService = inject(LoggingService);
    private readonly $urlService = inject(UrlService);
    private readonly $intentService = inject(IntentService);

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;

    private readonly destructor$ = new Subject<void>();
    chartConfig: ChartConfiguration<"bar">;
    readonly showChart = signal(false);

    readonly ChartTitle: string = "Outstanding Invoices by Fund";

    private _chartCriteria: IChartCriteria;

    isLoaded: boolean;

    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((chartId: string) =>
            {
                if (chartId !== this.chartId)
                {
                    return;
                }
                this.loadChartData();
            });
    }

    get chartId(): string | undefined
    {
        return this.chartCriteria?.ChartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria;
    }

    set chartCriteria(value: IChartCriteria) { this._chartCriteria = value; }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    get AsOfDate(): string
    {
        const date = this.chartCriteria?.Parameters?.find(x => x.Id === "InvoicesOutstandingDate")?.Value;
        return Utilities.toShortDateString(date);
    }

    get Balance(): string
    {
        if (this.chartCriteria?.ChartDatas)
        {
            return this.chartCriteria.ChartDatas[0].Data.reduce((a, b) => a + b, 0);
        }
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        new Promise(
            () =>
            {
                if (!this.hasData())
                {
                    return this.loadChartData();
                }
                this.updateChartConfiguration();
            })
            .finally(() =>
            {
                this.isLoaded = false;
            });
    }

    hasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0;
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                    if (!this.hasData())
                    {
                        return;
                    }

                    this.updateChartConfiguration();
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    openSettings(): void
    {
        this.$chartDialogService.showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    updateChartConfiguration(): void
    {
        this.chartConfig = {
            type: "bar",
            data: {
                datasets: [{
                    type: "bar",
                    data: this.chartCriteria.ChartDatas[0].Data,
                    backgroundColor: this.$chartService.getColors(20),
                    barPercentage: 0.8,
                }],
            },
            options: {
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            title: (tooltipItems: TooltipItem<"bar">[]): string | string[] =>
                            {
                                return this.chartCriteria.ChartDatas[0].Additional[tooltipItems[0].dataIndex].Item1;
                            },
                            label: (tooltipItem: TooltipItem<"bar">): string | string[] =>
                            {
                                return formatCurrency(Number(tooltipItem.raw), this.locale, "$");
                            },
                        },
                    },
                },
                onClick: (event: ChartEvent & Event, _elements: ActiveElement[], chart: Chart): void =>
                {
                    const points = chart.getElementsAtEventForMode(event, "nearest", { intersect: true }, true);
                    if (!points?.length)
                    {
                        return null;
                    }

                    const index = points[0].index;
                    const date = this.chartCriteria?.Parameters?.find(x => x.Id === "InvoicesOutstandingDate")?.Value;
                    this.$urlService
                        .GetAppLink({
                            App: "invoiceSearch",
                            Parms: [{
                                Key: "fund",
                                Value: this.chartCriteria.ChartDatas[0].Additional[index].Item2,
                            },
                            {
                                Key: "toPostDate",
                                Value: Utilities.toShortDateString(date),
                            },
                            {
                                Key: "outstanding",
                                Value: "true",
                            }],
                        })
                        .then((executionResult: ExecutionResult<string>) =>
                        {
                            const index = executionResult.Result.indexOf("?");
                            if (index > 0)
                            {
                                this.$intentService.resolve("Payables", "Search", {
                                    querystring: executionResult.Result.substring(index, executionResult.Result.length),
                                });
                            }
                        });
                },
                elements: { line: {} },
                scales: {
                    x: {
                        display: true,
                        grid: { display: true },
                        labels: this.chartCriteria.ChartDatas[0].Labels,
                    },
                    y: {
                        type: "linear",
                        display: true,
                        position: "left",
                        grid: { display: true },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(
                                    value > -10 && value < 10
                                        ? value.toFixed(2)
                                        : value.toFixed(0));
                            },
                        },
                    },
                },
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }
}
